/* ═══════════════════════════════════════════════════════════════════════════════
   WATRLY CUSTOMER LAYOUT — التخطيط الرئيسي للعميل
   بتصميم مائي زجاجي مذهل — Water Glass Design System
   ═══════════════════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import '../customer-theme.css';
import '../water-design-system.css';

// Import Water Icons
import {
  WaterHomeIcon,
  WaterListIcon,
  WaterWalletIcon,
  WaterProfileIcon,
  WaterMoonIcon,
  WaterSunIcon,
  DropletIcon,
} from '../components/WaterIcons';
import FloatingChat from '../components/FloatingChat';
import NotificationBell from '../components/NotificationBell';
import NotificationToast from '../components/NotificationToast';
import NotificationPermissionDialog from '../components/NotificationPermissionDialog';
import { requestPermission } from '../services/NotificationService';

const NAV = [
  { to: '/customer/home',    Icon: WaterHomeIcon,    label: 'الرئيسية' },
  { to: '/customer/orders',  Icon: WaterListIcon,    label: 'طلباتي'  },
  { to: '/customer/wallet',  Icon: WaterWalletIcon,  label: 'محفظتي'  },
  { to: '/customer/profile', Icon: WaterProfileIcon, label: 'حسابي'   },
];

export default function CustomerLayout() {
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/customer/login', { replace: true }); };
  const initials = (customer?.display_name || 'ع')[0];

  /* ── Request push permission on mount ── */
  useEffect(() => {
    if (customer) {
      setTimeout(() => requestPermission(), 2000);
    }
  }, [customer]);

  /* ── Theme toggle ── */
  const [theme, setTheme] = useState(() => localStorage.getItem('wt-theme') || 'dark');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('wt-theme', theme);
  }, [theme]);
  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');
  const isLight = theme === 'light';

  /* ── Adaptive nav: dark when theme is dark, light when theme is light ── */
  const navRef = useRef(null);
  // navDark = true → dark nav style (light icons), false → light nav (dark icons)
  const navDark = theme === 'dark';

  /* ── Hide nav on sub-pages ── */
  const location = useLocation();
  const isSubPage = (() => {
    const p = location.pathname;
    if (p === '/customer/orders' || p === '/customer/addresses') return false;
    return (
      p.startsWith('/customer/orders/') ||
      p.startsWith('/customer/addresses/') ||
      p.startsWith('/customer/chat/')
    );
  })();
  const [navVisible, setNavVisible] = useState(!isSubPage);
  useEffect(() => {
    setNavVisible(!isSubPage);
  }, [isSubPage]);

  return (
    <div className="wt-root min-h-screen" style={{ background: 'var(--wt-bg, #03111e)' }}>
      {/* ══ Desktop Sidebar ══ */}
      <aside className="hidden lg:flex lg:flex-col fixed right-0 top-0 h-full z-30 w-[260px] bg-[var(--wt-sidebar-bg)] border-l border-[var(--wt-sidebar-border)] backdrop-blur-xl">
        {/* Logo + Theme Toggle */}
        <div className="px-5 py-5 border-b border-[var(--wt-sidebar-border)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img
                src={isLight ? '/assets/images/logo/blue-logo.png' : '/assets/images/logo/white-logo.png'}
                alt="وترلي"
                className="h-12 w-auto object-contain"
              />
            </div>
            <button 
              onClick={toggleTheme} 
              className="water-icon-btn w-8 h-8"
              title={isLight ? 'الوضع الداكن' : 'الوضع الفاتح'}
            >
              {isLight ? <WaterMoonIcon className="w-4 h-4" /> : <WaterSunIcon className="w-4 h-4" />}
            </button>
          </div>

          {/* User Card with Water Glass Effect */}
          <div className="glass-card p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[rgba(10,186,181,0.25)] to-[rgba(0,188,212,0.15)] border-2 border-[rgba(10,186,181,0.4)] flex items-center justify-center text-[#0ABAB5] font-bold text-sm flex-shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-[var(--wt-text-primary)] font-bold text-sm truncate">{customer?.display_name}</p>
              <p className="text-[var(--wt-text-muted)] text-xs" dir="ltr">{customer?.phone}</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4">
          {NAV.map(({ to, Icon, label }) => (
            <NavLink 
              key={to} 
              to={to} 
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl mb-1 font-bold text-sm transition-all duration-300
                ${isActive 
                  ? 'text-white bg-gradient-to-r from-[#0ABAB5]/20 to-transparent border-r-2 border-[#0ABAB5] shadow-[0_0_20px_rgba(10,186,181,0.15)]' 
                  : 'text-[var(--wt-text-secondary)] hover:text-[var(--wt-text-primary)] hover:bg-[rgba(255,255,255,0.05)]'
                }
              `}
            >
              {({ isActive }) => (
                <>
                  <Icon className="w-7 h-7 flex-shrink-0" active={isActive} isDark={navDark} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="px-3 pb-6">
          <div className="h-px bg-[var(--wt-divider)] mb-3" />
          <button 
            onClick={handleLogout} 
            className="flex items-center gap-3 px-4 py-3 rounded-xl w-full font-bold text-sm text-[var(--wt-text-secondary)] hover:text-[#f43f5e] hover:bg-[rgba(244,63,94,0.1)] transition-all duration-300"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 15l3-3m0 0l-3-3m3 3H9"/>
            </svg>
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* ══ Mobile Header ══ */}
      <header
        className="lg:hidden fixed top-0 inset-x-0 z-20 px-4 flex items-center justify-between border-b border-[var(--wt-header-border)] transition-colors duration-300"
        style={{
          background: 'var(--wt-header-bg)',
          backdropFilter: 'blur(32px) saturate(200%)',
          WebkitBackdropFilter: 'blur(32px) saturate(200%)',
          transform: navVisible ? 'translateY(0)' : 'translateY(-100%)',
          opacity: navVisible ? 1 : 0,
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
          pointerEvents: navVisible ? 'auto' : 'none',
          paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
          paddingBottom: '0.75rem',
        }}
      >
        <div className="flex items-center gap-2.5">
          <img
            src={isLight ? '/assets/images/logo/blue-logo.png' : '/assets/images/logo/white-logo.png'}
            alt="وترلي"
            className="h-10 w-auto object-contain"
          />
        </div>

        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} className="water-icon-btn w-7 h-7">
            {isLight ? <WaterMoonIcon className="w-4 h-4" /> : <WaterSunIcon className="w-4 h-4" />}
          </button>
          <NotificationBell isDark={!isLight} />
        </div>
      </header>

      {/* ══ Main ══ */}
      <main className="lg:mr-[260px] min-h-screen" style={{ paddingBottom: navVisible ? '8rem' : '1.5rem', paddingTop: navVisible ? 'calc(4rem + env(safe-area-inset-top, 0px))' : '0', transition: 'padding 0.3s ease' }}>
        <div className="max-w-3xl mx-auto px-4 py-5">
          <Outlet />
        </div>
      </main>

      {/* ══ Floating Live Chat ══ */}
      <FloatingChat />

      {/* ══ In-app Notification Toasts ══ */}
      <NotificationToast isDark={!isLight} />

      {/* ══ Notification Permission Dialog ══ */}
      <NotificationPermissionDialog isDark={!isLight} />

      {/* ══ Mobile Floating Bottom Nav ══ */}
      <nav
        ref={navRef}
        className="lg:hidden fixed bottom-4 left-4 right-4 z-50"
        style={{
          transform: navVisible ? 'translateY(0)' : 'translateY(120px)',
          opacity: navVisible ? 1 : 0,
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease, background 0.4s ease',
          pointerEvents: navVisible ? 'auto' : 'none',
        }}
      >
        <div
          style={{
            borderRadius: '1.25rem',
            padding: '0.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            backdropFilter: 'blur(32px) saturate(200%)',
            WebkitBackdropFilter: 'blur(32px) saturate(200%)',
            background: 'var(--wt-nav-bg)',
            border: '1px solid var(--wt-nav-border)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.25), var(--wt-glow-sm, none)',
          }}
        >
          {NAV.map(({ to, Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className="flex flex-col items-center justify-center gap-0.5 px-3 py-2 rounded-xl transition-all duration-300"
              style={({ isActive }) => ({
                color: isActive ? '#0ABAB5' : navDark ? 'rgba(255,255,255,0.6)' : 'rgba(30,40,60,0.65)',
                background: isActive
                  ? navDark ? 'rgba(10,186,181,0.18)' : 'rgba(10,186,181,0.14)'
                  : 'transparent',
                minWidth: '64px',
              })}
            >
              {({ isActive }) => (
                <div className="flex flex-col items-center justify-center" style={{ pointerEvents: 'none' }}>
                  <Icon className="w-6 h-6" active={isActive} isDark={navDark} />
                  <span style={{
                    fontSize: '0.6rem', fontWeight: 700,
                    fontFamily: "'Cairo', sans-serif",
                    color: isActive ? '#0ABAB5' : navDark ? 'rgba(255,255,255,0.6)' : 'rgba(30,40,60,0.65)',
                    marginTop: '2px',
                  }}>{label}</span>
                </div>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

